
import React, { useEffect } from "react";
import {
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  TextField,
} from "@mui/material";
import {
  Add,
  CheckBoxOutlineBlankOutlined,
  CheckBoxOutlined,
  Delete,
} from "@mui/icons-material";
import { Controller, useFieldArray, useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import moment from "moment";

import {
  VEHICLE_MAINTENANCE_TYPES,
  VEHICLE_MAINTENANCE_CONDITIONS,
} from "data/static/staticOptions";

import AppForm from "components/Shared/AppForm";
import BaseSelect from "components/Shared/Base/BaseSelect";
import AppSwitch from "components/Shared/AppSwitch";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import FileDropzone from "components/Shared/FileDropzone";

import { useLazyGetDDLVehicleListQuery } from "store/services/vehicleApi";
import { useLazyGetDDLEntityListQuery } from "store/services/entityApi";
import { useGetDDLAccountListQuery } from "store/services/accountApi";
import { useVehicleDetailContext } from "contexts/VehicleDetailContext";
import { useAuth, useDOUploadFile } from "hooks";
import { useLazyGetDDLMerchantListByTypesQuery } from "store/services/merchantApi";
import RichTextEditor from "components/Shared/RichTextEditor";

const INITIAL_STATE = {
  vehicleId: "",
  maintenanceType: "",
  merchant: "",
  maintenanceDate: "",
  cost: "",
  partsReplaced: [{ partName: "", partCost: "" }],
  nextMaintenanceDue: "",
  maintenanceCondition: "",
  account: "",
  entity: "",
  description: "",
  attachments: [],
  isActive: true,
};

const VehicleMaintenanceForm = ({
  isBusy = false,
  vehicleMaintenanceDetail = null,
  onSubmit = () => { },
}) => {
  const [showReplacePart, setShowReplacePart] = React.useState(false);
  const navigate = useNavigate();
  const { viewEntity, isAccountAdmin, isSystemAdmin } = useAuth();
  const { handleUploadFiles } = useDOUploadFile();

  // Safely access context, handle case where it might not be provided
  let vehicleDetail = null;
  try {
    const vehicleDetailContext = useVehicleDetailContext();
    vehicleDetail = vehicleDetailContext ? vehicleDetailContext.vehicleDetail : null;
  } catch (error) {
    console.warn("VehicleDetailContext not provided, using dropdown for vehicle selection.");
  }

  // React Hook Form
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      ...INITIAL_STATE,
      entity: viewEntity?.entity,
      account: viewEntity?.entity?.account,
      vehicleId: vehicleDetail ? {
        _id: vehicleDetail._id,
        vehicle_id: vehicleDetail.vehicle_id,
        registration_number: vehicleDetail.registration_number,
      } : "",
    },
  });

  const _watchEntity = useWatch({ control, name: "entity" });
  const _watchAccount = useWatch({ control, name: "account" });

  // RTK Query
  const { data: accounts, isLoading: loadingAccounts } = useGetDDLAccountListQuery();
  const [fetchEntities, { data: entities, isLoading: loadingEntities }] =
    useLazyGetDDLEntityListQuery();
  const [fetchVehicles, { data: vehicles, isLoading: loadingVehicles }] =
    useLazyGetDDLVehicleListQuery();
  const [fetchMerchants, { data: merchants, isLoading: loadingMerchants }] =
    useLazyGetDDLMerchantListByTypesQuery();
  useEffect(() => {
    if (_watchAccount?._id && _watchEntity?._id) {
      fetchMerchants({
        account: _watchAccount._id,
        entity: _watchEntity._id,
        merchantType: "Service Center",
        active: "true",
      });
    }
  }, [_watchAccount?._id, _watchEntity?._id, fetchMerchants]);
  useEffect(() => {
    if (_watchEntity?._id && !vehicleDetail) {
      fetchVehicles({ entityId: _watchEntity._id });
    }
  }, [_watchEntity, fetchVehicles, vehicleDetail]);

  useEffect(() => {
    if (vehicleDetail) {
      setValue("vehicleId", {
        _id: vehicleDetail._id,
        vehicle_id: vehicleDetail.vehicle_id,
        registration_number: vehicleDetail.registration_number,
      });
    }
  }, [vehicleDetail, setValue]);

  // Method to add partsReplaced
  const { fields, append, remove } = useFieldArray({
    control,
    name: "partsReplaced",
  });

  // Methods
  const handleOnVehicleMaintenanceSubmit = async (data) => {
    const _vehicleMaintenancePayLoad = {
      ...data,
      entity: data?.entity?._id,
      account: data?.account?._id,
      vehicleId: vehicleDetail?._id || data?.vehicleId?._id,
      merchant: data?.merchant?._id,
    };

    // Separate already uploaded and new files
    const [existingAttachments, newAttachments] = data?.attachments?.reduce(
      ([uploaded, pending], file) =>
        file?.publicId || file?.url
          ? [[...uploaded, file], pending]
          : [uploaded, [...pending, file]],
      [[], []]
    );

    // Upload new attachments if any
    if (newAttachments.length) {
      const { uploadedFiles } = await handleUploadFiles(newAttachments, {
        folderName: "VEHICLE_MAINTENANCE",
        entity: data?.entity?._id,
        account: data?.account?._id,
      });

      _vehicleMaintenancePayLoad.attachments = [
        ...existingAttachments,
        ...uploadedFiles,
      ];
    }

    onSubmit(_vehicleMaintenancePayLoad);
  };


  React.useEffect(() => {
    if (!vehicleMaintenanceDetail) return;
    // Set showReplacePart based on partsReplaced
    if (vehicleMaintenanceDetail.partsReplaced) {
      setShowReplacePart(true);
    }
    reset({
      ...vehicleMaintenanceDetail,
      ...(vehicleMaintenanceDetail?.nextMaintenanceDue && {
        nextMaintenanceDue: moment(
          vehicleMaintenanceDetail.nextMaintenanceDue
        ).format("YYYY-MM-DD"),
      }),
      ...(vehicleMaintenanceDetail?.maintenanceDate && {
        maintenanceDate: moment(
          vehicleMaintenanceDetail.maintenanceDate
        ).format("YYYY-MM-DD"),
      }),
      merchant: vehicleMaintenanceDetail.merchant || "",

    });
  }, [vehicleMaintenanceDetail, reset]);

  React.useEffect(() => {
    if (isAccountAdmin || isSystemAdmin) {
      fetchEntities({
        account: _watchAccount?._id ?? viewEntity?.entity?.account?._id?._id,
      });
    }
  }, [
    _watchAccount?._id,
    fetchEntities,
    isAccountAdmin,
    isSystemAdmin,
    viewEntity?.entity?.account,
  ]);

  React.useEffect(() => {
    if (!_watchEntity?._id || vehicleDetail) return;
    fetchVehicles({ account: _watchAccount?._id, entity: _watchEntity?._id });
  }, [_watchAccount?._id, _watchEntity?._id, fetchVehicles, vehicleDetail]);

  return (
    <AppForm loading={isBusy}>
      <AppForm.Content>
        <Grid container spacing={4}>
          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item md={6}>
              <Controller
                name="account"
                control={control}
                rules={{ required: "Please select an account." }}
                render={({ field }) => (
                  <BaseAutocomplete
                    {...field}
                    fullWidth
                    label="Account"
                    required
                    value={field.value ?? ""}
                    onChange={(_, data) => {
                      field.onChange(data);
                      setValue("entity", "");
                      setValue("vehicleId", "");
                      setValue("merchant", "");

                    }}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={accounts || []}
                    loading={loadingAccounts}
                    loadingText="Loading Account..."
                    disabled={isAccountAdmin}
                    helperText={errors?.account?.message}
                    error={!!errors?.account}
                  />
                )}
              />
            </Grid>
          )}
          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item md={6}>
              <Controller
                name="entity"
                control={control}
                rules={{ required: "Please select an entity." }}
                render={({ field: { value, onChange } }) => (
                  <BaseAutocomplete
                    fullWidth
                    required
                    label="Entity"
                    value={value ?? ""}
                    onChange={(_, data) => {
                      onChange(data);
                      setValue("vehicleId", "");
                      setValue("merchant", "");

                    }}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={entities || []}
                    loading={loadingEntities}
                    loadingText="Loading Entities..."
                    helperText={errors?.entity?.message}
                    error={!!errors?.entity}
                  />
                )}
              />
            </Grid>
          )}
          <Grid item md={6}>
            <Controller
              name="vehicleId"
              control={control}
              rules={{ required: "Please select Vehicle." }}
              render={({ field: { value, onChange } }) => (
                <BaseAutocomplete
                  fullWidth
                  label="Vehicle"
                  required
                  value={
                    vehicles?.find((vec) => vec._id === value?._id) ?? value ?? null
                  }
                  onChange={(_, data) => onChange(data)}
                  getOptionLabel={(opt) =>
                    opt ? `${opt.vehicle_id} - ${opt.registration_number}` : ""
                  }
                  isOptionEqualToValue={(option, value) =>
                    option?._id === value?._id
                  }
                  options={vehicles || []}
                  loading={loadingVehicles}
                  loadingText="Loading Vehicles..."
                  helperText={errors?.vehicleId?.message}
                  error={!!errors?.vehicleId}
                  disabled={!!vehicleDetail} // Disable if vehicleDetail is provided via context
                />
              )}
            />
          </Grid>
          <Grid item md={6}>
            <Controller
              name="merchant"
              control={control}
              rules={{ required: "Please select a service center." }}
              render={({ field: { value, onChange } }) => (
                <BaseAutocomplete
                  fullWidth
                  label="Service Center"
                  required
                  value={
                    merchants?.find((m) => m._id === value?._id) ?? value ?? null
                  }
                  onChange={(_, data) => onChange(data)}
                  getOptionLabel={(opt) =>
                    opt ? `${opt.merchantName} (${opt.merchantCode})` : ""
                  }
                  isOptionEqualToValue={(option, value) =>
                    option?._id === value?._id
                  }
                  options={merchants || []}
                  loading={loadingMerchants}
                  loadingText="Loading Service Centers..."
                  helperText={errors?.merchant?.message}
                  error={!!errors?.merchant}
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="maintenanceType"
              control={control}
              rules={{ required: "Maintenance ID is required" }}
              render={({ field }) => (
                <BaseSelect
                  {...field}
                  required
                  fullWidth
                  size="small"
                  label="Maintenance Type"
                  options={VEHICLE_MAINTENANCE_TYPES}
                  helperText={errors?.maintenanceType?.message}
                  error={!!errors?.maintenanceType}
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="maintenanceDate"
              control={control}
              rules={{ required: "Maintenance Date is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  size="small"
                  label="Maintenance Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={
                    field.value ? moment(field.value).format("YYYY-MM-DD") : ""
                  }
                  helperText={errors?.maintenanceDate?.message}
                  error={!!errors?.maintenanceDate}
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="cost"
              control={control}
              rules={{
                required: 'Maintenance Cost is required',
                min: {
                  value: 0,
                  message: 'Maintenance Cost must be a non-negative number',
                },
                validate: (value) => !isNaN(value) || 'Maintenance Cost must be a valid number',
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  size="small"
                  label="Maintenance Cost"
                  type="number"
                  inputProps={{ min: 0 }}
                  error={!!errors.cost}
                  helperText={errors?.cost?.message}
                  onChange={(e) => {
                    const value = e.target.value ? parseFloat(e.target.value) : '';
                    field.onChange(value);
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="nextMaintenanceDue"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  label="Next Maintenance Due (Optional)"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={
                    field.value ? moment(field.value).format("YYYY-MM-DD") : ""
                  }
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="maintenanceCondition"
              control={control}
              rules={{ required: "Maintenance Condition is Required" }}
              render={({ field }) => (
                <BaseSelect
                  {...field}
                  required
                  fullWidth
                  size="small"
                  label="Maintenance Condition"
                  options={VEHICLE_MAINTENANCE_CONDITIONS}
                  helperText={errors?.maintenanceCondition?.message}
                  error={!!errors?.maintenanceCondition}
                />
              )}
            />
          </Grid>
          {/* Parts Replaced */}
          <Grid item xs={5} ml={1}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showReplacePart}
                  onChange={(e) => setShowReplacePart(e.target.checked)}
                  icon={<CheckBoxOutlineBlankOutlined />}
                  checkedIcon={<CheckBoxOutlined />}
                />
              }
              label="Add Maintenance Replace Parts"
            />
          </Grid>

          <Grid item xs={12}>
            <Grid container spacing={4}>
              <Grid item xs={6}>
                <div style={{ color: "gray", fontSize: 15, marginBottom: ".75rem" }}>
                  Description
                </div>
                <Controller
                  name="description"
                  control={control}
                  render={({ field: { onChange } }) => (
                    <RichTextEditor
                      initialValue={vehicleMaintenanceDetail?.description || "<p>Enter description .</p>"}
                      onChange={(value) => {
                        const plainText = value.replace(/<[^>]+>/g, "");
                        onChange(plainText);
                      }}
                      error={!!errors?.description}
                      helperText={errors?.description?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                <div style={{ color: "gray", fontSize: 15, marginBottom: ".75rem" }}>
                  Attachments
                </div>
                <Controller
                  name="attachments"
                  control={control}
                  render={({ field }) => (
                    <FileDropzone
                      multiple
                      maxFiles={5}
                      files={field?.value ?? []}
                      onFileChange={(files) => setValue("attachments", files)}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Grid>

          {vehicleMaintenanceDetail && (
            <Grid item xs={6}>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <AppSwitch {...field} label="Is Active?" />
                )}
              />
            </Grid>
          )}
          {showReplacePart && (
            <Grid item xs={12}>
              <Grid container spacing={2}>
                {fields.map((field, index) => (
                  <Grid
                    key={field.id}
                    container
                    item
                    xs={12}
                    spacing={4}
                    alignItems="center"
                  >
                    <Grid item xs={5}>
                      <Controller
                        name={`partsReplaced.${index}.partName`}
                        control={control}
                        rules={{ required: "Part Name is required" }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            required
                            size="small"
                            label="Part Name"
                            error={!!errors?.partsReplaced?.[index]?.partName}
                            helperText={
                              errors?.partsReplaced?.[index]?.partName?.message
                            }
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={5}>
                      <Controller
                        name={`partsReplaced.${index}.partCost`}
                        control={control}
                        rules={{
                          required: "Part Cost is required",
                          validate: (value) =>
                            value > 0 || "Part Cost must be greater than 0",
                        }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            required
                            size="small"
                            type="number"
                            label="Part Cost"
                            error={!!errors?.partsReplaced?.[index]?.partCost}
                            helperText={
                              errors?.partsReplaced?.[index]?.partCost?.message
                            }
                          />
                        )}
                      />
                    </Grid>

                    <Grid
                      item
                      xs={1}
                      display="flex"
                      justifyContent="flex-end"
                      gap={2}
                      alignItems="center"
                      sx={{ paddingBottom: 1 }}
                    >
                      <IconButton
                        aria-label="delete"
                        color="error"
                        onClick={() => remove(index)}
                        sx={{
                          height: "35px",
                          width: "35px",
                          "&:hover": {
                            backgroundColor: "rgba(255, 0, 0, 0.2)",
                            transform: "scale(1.1)",
                          },
                        }}
                      >
                        <Delete />
                      </IconButton>

                      <IconButton
                        aria-label="add"
                        color="primary"
                        onClick={() => append({ partName: "", partCost: "" })}
                        sx={{
                          height: "35px",
                          width: "35px",
                          "&:hover": {
                            backgroundColor: "rgba(0, 123, 255, 0.2)",
                            transform: "scale(1.1)",
                          },
                        }}
                      >
                        <Add />
                      </IconButton>
                    </Grid>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          )}
        </Grid>
      </AppForm.Content>
      <AppForm.Actions>
        <Button onClick={() => navigate(-1)}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit(handleOnVehicleMaintenanceSubmit)}
        >
          {vehicleMaintenanceDetail ? "Update" : "Add"}
        </Button>
      </AppForm.Actions>
    </AppForm>
  );
};

export default VehicleMaintenanceForm;